/**
 * The InOut calculator — the first number is free and needs no account (AC-1:
 * the dashboard's engine on the record the person describes, nothing saved).
 * One question at a time (`useCalculatorWizard`): the host country, the title,
 * its dates, the trips — then the answer, read as the dashboard reads it.
 *
 * The same calculator for every host — Tonnie's public page, a partner's
 * portal (embedded), a modal over Citizen Web, the app's sheet. The host says
 * whose it is (`brand`), what keeping the answer says and does (`keep`), and
 * whether it can be closed.
 */
import { useLocale } from '@tonnie/locale';
import { useEffect, type ReactNode } from 'react';

import {
  useCalculatorWizard,
  type CalculatorStep,
  type CalculatorWizard,
} from '../../hooks/use-calculator-wizard';
import type { InOutKeptCalculation } from '../../lib/kept-calculation';
import { CalculatorFrame, type CalculatorBrand } from './CalculatorFrame';
import { CalculatorResult, type CalculatorKeepCopy } from './CalculatorResult';
import { CountryStep, DatesStep, TitleStep, TripsStep } from './CalculatorSteps';

export interface InOutCalculatorProps {
  /** What keeping the answer says and does — the host's. */
  keep: CalculatorKeepCopy & { run: (kept: InOutKeptCalculation) => void };
  /** The country already chosen (a portal's own) — still changeable on the first question. */
  startCountry?: string | null;
  brand?: CalculatorBrand | null;
  headerEnd?: ReactNode;
  onClose?: () => void;
  /** The calculator is the page (a partner's portal, the app's sheet). */
  standalone?: boolean;
  /**
   * Whether there are answers closing would lose — so what holds the
   * calculator asks before it closes it.
   */
  onAnswersChange?: (hasAnswers: boolean) => void;
}

/** Each screen's question and the line under it. */
function useScreenText(step: CalculatorStep): { title: string; hint: string } {
  const { t } = useLocale();
  switch (step) {
    case 'country':
      return {
        title: t('inout.calculator.country.question'),
        hint: t('inout.calculator.country.hint'),
      };
    case 'title':
      return {
        title: t('inout.calculator.title.question'),
        hint: t('inout.calculator.title.hint'),
      };
    case 'dates':
      return {
        title: t('inout.calculator.dates.question'),
        hint: t('inout.calculator.dates.hint'),
      };
    case 'trips':
      return {
        title: t('inout.calculator.trips.question'),
        hint: t('inout.calculator.trips.hint'),
      };
    case 'result':
      return {
        title: t('inout.calculator.result.title'),
        hint: t('inout.calculator.result.subtitle'),
      };
    default: {
      const unreachable: never = step;
      throw new Error(`Unknown calculator step: ${String(unreachable)}`);
    }
  }
}

export function InOutCalculator({
  keep,
  startCountry,
  brand,
  headerEnd,
  onClose,
  standalone,
  onAnswersChange,
}: InOutCalculatorProps) {
  const wizard = useCalculatorWizard({ startCountry, onKeep: keep.run });
  // The answer is read only once calculated: without one, the trips are the last question.
  const step: CalculatorStep = wizard.step === 'result' && !wizard.answer ? 'trips' : wizard.step;
  const screen = useScreenText(step);
  const { hasAnswers } = wizard;
  useEffect(() => onAnswersChange?.(hasAnswers), [hasAnswers, onAnswersChange]);

  return (
    <CalculatorFrame
      step={step}
      title={screen.title}
      description={screen.hint}
      brand={brand}
      onBack={step === 'country' ? undefined : wizard.back}
      headerEnd={headerEnd}
      onClose={onClose}
      standalone={standalone}
    >
      <Screen step={step} wizard={wizard} keep={keep} />
    </CalculatorFrame>
  );
}

function Screen({
  step,
  wizard,
  keep,
}: {
  step: CalculatorStep;
  wizard: CalculatorWizard;
  keep: CalculatorKeepCopy;
}) {
  switch (step) {
    case 'country':
      return <CountryStep wizard={wizard} />;
    case 'title':
      return <TitleStep wizard={wizard} />;
    case 'dates':
      return <DatesStep wizard={wizard} />;
    case 'trips':
      return <TripsStep wizard={wizard} />;
    case 'result':
      return <CalculatorResult wizard={wizard} keep={keep} />;
    default: {
      const unreachable: never = step;
      throw new Error(`Unknown calculator step: ${String(unreachable)}`);
    }
  }
}
